export type UUID = `${string}-${string}-${string}-${string}-${string}`;

export enum Role {
  SYSTEM = "system",
  USER = "user",
}

export enum ModalityType {
  TEXT = "text",
  IMAGE = "image",
  AUDIO = "audio",
  VIDEO = "video",
  TRAJECTORY = "trajectory",
}

export enum ImageTypes {
  PNG = "png",
  JPG = "jpg",
  JPEG = "jpeg",
  WEBP = "webp",
}

export enum VideoTypes {
  MP4 = "mp4",
  WEBM = "webm",
  OGG = "ogg",
  AVI = "avi",
  MOV = "mov",
  WMV = "wmv",
  FLV = "flv",
}

export interface MediaData {
  data: string;
  extension: string;
  key: string;
  url?: string;
  documentIndex?: number | null;
}

export interface Metadata {
  [key: string]: string | number | boolean | null | undefined;
}

export interface DataAtomic {
  metadata?: Metadata;
  role?: Role;
}

export interface Text extends DataAtomic {
  type: ModalityType.TEXT;
  content?: string;
}

export interface BranchText {
  type: "BranchText";
  content: Text[];
  metadata?: Metadata;
}

// Base64 validation function
function validateBase64(value: string | undefined): string | undefined {
  if (!value) return undefined;

  // Check if the string is valid base64
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  if (!base64Regex.test(value)) {
    throw new Error("Invalid base64 string");
  }

  // Check if the string length is a multiple of 4
  if (value.length % 4 !== 0) {
    throw new Error("Base64 string length must be a multiple of 4");
  }

  // Try to decode the base64 string
  try {
    atob(value);
    return value;
  } catch {
    throw new Error("Invalid base64 string");
  }
}

// Type for base64 encoded content
type Base64Content = string & { readonly __brand: unique symbol };

// Type guard for base64 content
function isBase64Content(value: string): value is Base64Content {
  try {
    validateBase64(value);
    return true;
  } catch {
    return false;
  }
}

export interface Image extends DataAtomic {
  type: ModalityType.IMAGE;
  content?: Base64Content;
  metadata?: Metadata;
}

export interface Audio extends DataAtomic {
  type: ModalityType.AUDIO;
  content?: Base64Content;
  metadata?: Metadata;
}

export interface Video extends DataAtomic {
  type: ModalityType.VIDEO;
  content?: Base64Content;
  tracks: Record<string, Event[]>;
  metadata?: Metadata;
}

type NestedNumberArray = number[] | number[][] | number[][][] | number[][][][] | number[][][][][] | number[][][][][][];

export interface SerializedArray {
  values: NestedNumberArray;
  dtype: string;
  shape: number[];
}

export interface StateVector {
  data: SerializedArray;
}

export interface Reference {
  type: "reference";
  index: number;
}

export interface Step {
  index: number;
  timestamp_seconds: number;
  observation: Record<string, StateVector | Image | Reference>;
  action?: StateVector | null;
  reward?: number | null;
  done?: boolean;
  truncated?: boolean;
  metadata?: Metadata;
}

export interface Trajectory extends DataAtomic {
  type: ModalityType.TRAJECTORY;
  content?: null;
  steps: Step[];
  references?: Array<Image | Video | Audio>;
  episode_index?: number | null;
  task?: string | null;
  task_index?: number | null;
  fps?: number | null;
  robot_type?: string | null;
}

export interface Event {
  start_time_seconds: number;
  end_time_seconds: number;
  type: ModalityType;
  content: Content;
}

// Event validation function
function validateEvent(event: Event): Event {
  // Validate time values
  if (typeof event.start_time_seconds !== "number" || typeof event.end_time_seconds !== "number") {
    throw new Error("Time values must be numbers");
  }

  if (event.end_time_seconds < event.start_time_seconds) {
    throw new Error("End time must be greater than or equal to start time");
  }

  // Validate type matching
  if (event.type !== event.content.type) {
    throw new Error(`Event type '${event.type}' must match content type '${event.content.type}'`);
  }

  return event;
}

// Type guard for Event
function isValidEvent(value: unknown): value is Event {
  try {
    validateEvent(value as Event);
    return true;
  } catch {
    return false;
  }
}

export interface Document {
  content: Content[];
  metadata?: Metadata;
}

// Type for the discriminated union of content types
export type Content = Text | BranchText | Image | Audio | Video | Trajectory;

// Document validation function
function validateDocument(document: Document): Document {
  // Validate content array
  if (!Array.isArray(document.content)) {
    throw new Error("Document content must be an array");
  }

  // Validate each content item
  document.content.forEach((item, index) => {
    // Validate content type
    if (!item || typeof item !== "object") {
      throw new Error(`Invalid content item at index ${index}`);
    }

    // Validate BranchText content
    if (item.type === "BranchText") {
      const branchText = item as BranchText;
      if (!Array.isArray(branchText.content)) {
        throw new Error(`BranchText content must be an array at index ${index}`);
      }
      branchText.content.forEach((text, textIndex) => {
        if (text.type !== ModalityType.TEXT) {
          throw new Error(`BranchText can only contain Text content at index ${index}.${textIndex}`);
        }
      });
    }

    // Validate base64 content for media types
    if ([ModalityType.IMAGE, ModalityType.AUDIO, ModalityType.VIDEO].includes(item.type as ModalityType)) {
      const mediaItem = item as Image | Audio | Video;
      if (mediaItem.content) {
        validateBase64(mediaItem.content);
      }
    }

    // Validate video tracks
    if (item.type === ModalityType.VIDEO) {
      const video = item as Video;
      if (!video.tracks || typeof video.tracks !== "object") {
        throw new Error(`Video tracks must be an object at index ${index}`);
      }
      Object.entries(video.tracks).forEach(([trackName, events]) => {
        if (!Array.isArray(events)) {
          throw new Error(`Video track '${trackName}' must be an array at index ${index}`);
        }
        events.forEach((event) => {
          validateEvent(event);
        });
      });
    }
  });

  return document;
}

// Type guard for Document
function isValidDocument(value: unknown): value is Document {
  try {
    validateDocument(value as Document);
    return true;
  } catch {
    return false;
  }
}

// Export the validators and type guards for use in other files
export { validateBase64, isBase64Content, validateEvent, isValidEvent, validateDocument, isValidDocument };
